import type { Employee } from './types';
import { resolveOnboardingScreen } from './onboarding-flow';

/** Pairing is independent of whether Firebase can deliver a wake-up push. */
export function isPhoneLinked(employee: Pick<Employee, 'token' | 'deviceId'>): boolean {
  return Boolean(employee.token?.trim() && employee.deviceId?.trim());
}

/** CallSync is on the phone: it opened with this person's code, or the phone already linked. */
export function isAppInstalled(employee: Pick<Employee, 'token' | 'deviceId' | 'appInstalledAt'>): boolean {
  return isPhoneLinked(employee) || Boolean(employee.appInstalledAt?.trim());
}

export function usablePairingCode(
  employee: Pick<Employee, 'pairingCode' | 'pairingCodeExpiresAt'>,
  now = Date.now(),
): string {
  const code = employee.pairingCode?.trim() || '';
  if (!/^\d{6}$/.test(code)) return '';
  if (
    employee.pairingCodeExpiresAt &&
    (!Number.isFinite(Date.parse(employee.pairingCodeExpiresAt)) ||
      Date.parse(employee.pairingCodeExpiresAt) <= now)
  )
    return '';
  return code;
}

export const CONNECTION_POLL_MS = 10_000;

export function connectionPollInterval(
  screen: string | null,
  employee?: Pick<Employee, 'token' | 'deviceId'>,
): number | false {
  const linked = Boolean(employee && isPhoneLinked(employee));
  const visible = resolveOnboardingScreen(screen, employee && !linked ? 'install' : 'intro', {
    hasPerson: Boolean(employee),
    connected: linked,
    hasCall: false,
    complete: false,
  });
  // Install and Link & sync move on by themselves as the phone installs and links, so check often there.
  return visible === 'install' || visible === 'link' ? CONNECTION_POLL_MS : false;
}

/** Install moves on when the open screen first sees the app installed (or the phone linked). */
export function shouldAdvanceInstall(
  previous: { id: number; installed: boolean } | null,
  current: { id: number; installed: boolean },
): boolean {
  return Boolean(previous && previous.id === current.id && !previous.installed && current.installed);
}
